<template>
    <div class="fixed overflow-y-auto z-40 inset-0 p-6" ref="modal" v-if="exists">
        <transition
            enter-active-class="ease-out duration-300"
            enter-class="opacity-0"
            enter-to-class="opacity-100"
            leave-active-class="ease-in duration-200"
            leave-class="opacity-100"
            leave-to-class="opacity-0"
        >
            <div
                class="fixed inset-0 transition-opacity"
                :style="{ marginRight: `${scrollbarWidth}px` }"
                @click="onOverlayClick"
                v-if="opened"
            >
                <div class="absolute inset-0 bg-gray-600 opacity-75" />
            </div>
        </transition>

        <transition
            enter-active-class="ease-out duration-300"
            enter-class="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            enter-to-class="opacity-100 translate-y-0 sm:scale-100"
            leave-active-class="ease-in duration-200"
            leave-class="opacity-100 translate-y-0 sm:scale-100"
            leave-to-class="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            @after-enter="onOpened"
            @after-leave="onClosed"
        >
            <div class="flex justify-center" :class="{ 'min-h-full items-center': centered }" v-if="opened">
                <div
                    class="w-full bg-white rounded-md shadow-xl | transform transition-all"
                    :class="sizeClassName"
                >
                    <div class="p-4 border-b border-gray-200" v-if="title">
                        <h2 class="text-xl text-center font-medium" v-text="title" />
                    </div>
                    <div :class="bodyClassName">
                        <slot />
                    </div>
                    <div class="flex text-right space-x-2 border-t border-gray-200 bg-gray-100 rounded-b-md p-4 | md:block" v-if="$slots.footer">
                        <slot name="footer" />
                    </div>
                </div>
            </div>
        </transition>
    </div>
</template>

<script>
    export default {
        props: {
            name: {
                type: String,
                required: true,
            },
            title: {
                type: String,
                default: '',
            },
            clickToClose: {
                type: Boolean,
                default: true,
            },
            size: {
                type: String,
                default: 'md',
                validator: (value) => ['lg', 'md', 'sm'].includes(value),
            },
            centered: {
                type: Boolean,
                default: true,
            },
            bodyClassName: {
                type: String,
                default: 'p-6',
            },
        },

        data() {
            return {
                exists: false,
                opened: false,
                scrollbarWidth: 0,
                resizeObserver: null,
            };
        },

        computed: {
            sizeClassName() {
                switch (this.size) {
                    case 'sm':
                        return 'sm:max-w-md';
                    case 'md':
                        return 'sm:max-w-2xl';
                    case 'lg':
                    default:
                        return 'sm:max-w-4xl';
                }
            },
        },

        watch: {
            exists() {
                if (!this.exists) {
                    if (this.resizeObserver) {
                        this.resizeObserver.disconnect();
                        this.resizeObserver = null;
                    }

                    return;
                }

                // Hack to show the scrollbar correctly.
                this.resizeObserver = new ResizeObserver(entries => {
                    entries.forEach(entry => {
                        this.scrollbarWidth = entry.target.offsetWidth - entry.target.clientWidth;
                    });
                });

                this.$nextTick(() => {
                    this.resizeObserver.observe(this.$refs.modal);
                });
            },
        },

        methods: {
            toggle(isOpening, params) {
                if (this.opened === isOpening) {
                    return;
                }

                if (isOpening) {
                    this.open(params);
                } else {
                    this.close(params);
                }
            },

            open(params) {
                this.exists = true;

                this.$nextTick(() => {
                    this.opened = true;
                    this.$emit('open', params);
                });
            },

            close(params) {
                this.opened = false;
                this.$emit('close', params);
            },

            onToggle({ name, params, state = undefined }) {
                if (name == this.name) {
                    const nextState = typeof state === 'undefined' ? !this.opened : state;
                    this.toggle(nextState, params);
                }
            },

            onOpened() {
                this.$emit('opened');
            },

            onClosed() {
                this.$emit('closed');
                this.$errors.flush();

                this.$nextTick(() => {
                    this.exists = false;
                });
            },

            onOverlayClick() {
                if (this.clickToClose) {
                    this.close();
                }
            },

            onKeydown(e) {
                if (this.opened && this.clickToClose && e.key === 'Escape') {
                    this.close();
                }
            },
        },

        beforeMount() {
            this.$modal.bus.on('toggle', this.onToggle);
            document.addEventListener('keydown', this.onKeydown);
        },

        beforeDestroy() {
            this.$modal.bus.off('toggle', this.onToggle);
            document.removeEventListener('keydown', this.onKeydown);
        },
    };
</script>
